import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Wine, Utensils, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { useReservation } from '../context/ReservationContext';

const IMAGES = {
  hero: 'https://customer-assets.emergentagent.com/job_vino-vibes/artifacts/a16m95v3_FB_IMG_1770407268708.jpg',
  interior1: 'https://customer-assets.emergentagent.com/job_vino-vibes/artifacts/btdsd6oa_FB_IMG_1770407271743.jpg',
  seating: 'https://customer-assets.emergentagent.com/job_vino-vibes/artifacts/sej0xcfn_FB_IMG_1770407274720.jpg',
  cocktails: 'https://customer-assets.emergentagent.com/job_vino-vibes/artifacts/542rn9ub_FB_IMG_1770407386693.jpg',
  detail: 'https://customer-assets.emergentagent.com/job_vino-vibes/artifacts/mpggldq6_FB_IMG_1770407284759.jpg',
};

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const Home = () => {
  const { t } = useLanguage();
  const reservation = useReservation();

  return (
    <main data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${IMAGES.hero})` }}
        />
        <div className="absolute inset-0 hero-overlay" />
        
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <motion.h1 
            {...fadeInUp}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="text-5xl md:text-7xl lg:text-8xl font-bold text-[#EDEDED] mb-4 tracking-wider"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            <span className="text-6xl md:text-8xl lg:text-9xl">B</span>ar<span className="text-6xl md:text-8xl lg:text-9xl">B</span>aa<span className="text-6xl md:text-8xl lg:text-9xl">R</span>
          </motion.h1>
          <motion.p 
            {...fadeInUp}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="font-body text-lg md:text-xl lg:text-2xl text-[#C8A46A] mb-6 tracking-[0.15em]"
          >
            {t.hero.tagline}
          </motion.p>
          <motion.p 
            {...fadeInUp}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="font-accent italic text-xl md:text-2xl text-[#A1A1A1] mb-10"
          >
            {t.hero.location}
          </motion.p>
          <motion.div 
            {...fadeInUp}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button 
              asChild
              className="bg-copper hover:bg-copper-dark text-white font-body text-sm tracking-wider px-8 py-6 rounded-sm btn-copper"
              data-testid="hero-menu-btn"
            >
              <Link to="/menu">
                {t.hero.viewMenu} <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button 
              onClick={() => reservation.open()}
              className="bg-[#EDEDED] hover:bg-white text-[#0A0A0A] font-body text-sm tracking-wider px-8 py-6 rounded-sm"
              data-testid="hero-reserve-btn"
            >
              {t.hero.reserve}
            </Button>
          </motion.div>
        </div>

        <motion.div 
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-[#A1A1A1] rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-copper rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* About Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img 
                  src={IMAGES.seating} 
                  alt="BARBAAR interieur" 
                  className="w-full h-full object-cover image-hover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-48 h-48 border border-copper opacity-30" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="font-body text-sm tracking-[0.2em] text-copper mb-4 uppercase">{t.about.welcome}</p>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#EDEDED] mb-6">
                {t.about.title1} <br />
                <span className="text-copper-gradient">{t.about.title2}</span>
              </h2>
              <p className="font-body text-[#A1A1A1] text-lg leading-relaxed mb-8">
                {t.about.description}
              </p>
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center">
                  <Wine className="w-8 h-8 text-copper mx-auto mb-3" />
                  <p className="font-heading text-sm text-[#EDEDED] uppercase tracking-wide">{t.about.cocktails}</p>
                </div>
                <div className="text-center">
                  <Utensils className="w-8 h-8 text-copper mx-auto mb-3" />
                  <p className="font-heading text-sm text-[#EDEDED] uppercase tracking-wide">{t.about.sharingDishes}</p>
                </div>
                <div className="text-center">
                  <Clock className="w-8 h-8 text-copper mx-auto mb-3" />
                  <p className="font-heading text-sm text-[#EDEDED] uppercase tracking-wide">{t.about.lateNight}</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Section */}
      <section className="py-24 md:py-32 bg-[#141414]" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="font-body text-sm tracking-[0.2em] text-copper mb-4 uppercase">{t.specialties.discover}</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#EDEDED]">
              {t.specialties.title}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="glass-card p-8"
            >
              <div className="w-12 h-12 bg-copper/10 rounded-sm flex items-center justify-center mb-6">
                <Wine className="w-6 h-6 text-copper" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#EDEDED] mb-3">{t.specialties.cocktailsTitle}</h3>
              <p className="font-body text-[#A1A1A1] mb-6">{t.specialties.cocktailsDesc}</p>
              <div className="space-y-2">
                <div className="flex items-center">
                  <span className="font-body text-sm text-[#EDEDED]">Million Dollar Cocktail</span>
                </div>
                <div className="flex items-center">
                  <span className="font-body text-sm text-[#EDEDED]">Garden of Eden</span>
                </div>
                <div className="flex items-center">
                  <span className="font-body text-sm text-[#EDEDED]">Midnight Jazz</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="glass-card p-8"
            >
              <div className="w-12 h-12 bg-copper/10 rounded-sm flex items-center justify-center mb-6">
                <Utensils className="w-6 h-6 text-copper" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#EDEDED] mb-3">{t.specialties.sharingTitle}</h3>
              <p className="font-body text-[#A1A1A1] mb-6">{t.specialties.sharingDesc}</p>
              <div className="space-y-2">
                <div className="flex items-center">
                  <span className="font-body text-sm text-[#EDEDED]">Barbaar Signature Nacho's</span>
                </div>
                <div className="flex items-center">
                  <span className="font-body text-sm text-[#EDEDED]">Gegrilde Langoustines</span>
                </div>
                <div className="flex items-center">
                  <span className="font-body text-sm text-[#EDEDED]">BBQ Pork Belly</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="glass-card p-8"
            >
              <div className="w-12 h-12 bg-copper/10 rounded-sm flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-copper" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#EDEDED] mb-3">{t.specialties.winesTitle}</h3>
              <p className="font-body text-[#A1A1A1] mb-6">{t.specialties.winesDesc}</p>
              <div className="space-y-2">
                <div className="flex items-center">
                  <span className="font-body text-sm text-[#EDEDED]">Jacquart Champagne</span>
                </div>
                <div className="flex items-center">
                  <span className="font-body text-sm text-[#EDEDED]">Gassier Rosé</span>
                </div>
                <div className="flex items-center">
                  <span className="font-body text-sm text-[#EDEDED]">Chateau Maquin St-Emilion</span>
                </div>
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Button 
              asChild
              className="bg-copper hover:bg-copper-dark text-white font-body text-sm tracking-wider px-8 py-6 rounded-sm btn-copper"
              data-testid="featured-menu-btn"
            >
              <Link to="/menu">
                {t.specialties.fullMenu} <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="gallery-preview">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="font-body text-sm tracking-[0.2em] text-copper mb-4 uppercase">{t.gallery.subtitle}</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#EDEDED]">
              {t.gallery.title}
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="col-span-2 row-span-2 overflow-hidden"
            >
              <img 
                src={IMAGES.hero} 
                alt="BARBAAR bar" 
                className="w-full h-full object-cover image-hover"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="overflow-hidden"
            >
              <img 
                src={IMAGES.interior1} 
                alt="BARBAAR interieur" 
                className="w-full h-full object-cover aspect-square image-hover"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="overflow-hidden"
            >
              <img 
                src={IMAGES.detail} 
                alt="BARBAAR verlichting" 
                className="w-full h-full object-cover aspect-square image-hover"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="col-span-2 overflow-hidden"
            >
              <img 
                src={IMAGES.seating} 
                alt="BARBAAR zithoek" 
                className="w-full h-48 object-cover image-hover"
              />
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Button 
              asChild
              variant="outline"
              className="border-copper text-copper hover:bg-copper hover:text-white font-body text-sm tracking-wider px-8 py-6 rounded-sm"
              data-testid="gallery-preview-btn"
            >
              <Link to="/fotos">
                {t.gallery.viewAll} <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#141414] relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 opacity-10">
          <div 
            className="w-full h-full bg-cover bg-center"
            style={{ backgroundImage: `url(${IMAGES.interior1})` }}
          />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl font-bold text-[#EDEDED] mb-6"
          >
            {t.cta.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="font-body text-[#A1A1A1] text-lg mb-8"
          >
            Parijsstraat 26, 8430 Middelkerke
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button 
              asChild
              className="bg-copper hover:bg-copper-dark text-white font-body text-sm tracking-wider px-8 py-6 rounded-sm btn-copper"
              data-testid="cta-location-btn"
            >
              <Link to="/locatie">
                {t.cta.viewLocation}
              </Link>
            </Button>
            <Button 
              asChild
              variant="outline"
              className="border-[#2A2A2A] text-[#EDEDED] hover:bg-[#1F1F1F] font-body text-sm tracking-wider px-8 py-6 rounded-sm"
              data-testid="cta-contact-btn"
            >
              <Link to="/contact">
                {t.cta.sendMessage}
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default Home;
